//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import { getRightsRequestStatuses } from '@apis/gatewayMediaApi';
import { getMediaItemMetadata } from '@apis/universal-image-actions/universalImageActionsApi';
import Cuid from '@models/Cuid';
import IItemMetadata from '@models/IItemMetadata';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isDefined, isUndefined } from '@utils/filterPredicates';

export const useMediaMetadataQuery = (cuid: Cuid | undefined) =>
  useQuery<IItemMetadata>({
    queryKey: [
      QueryKeyConstants.UniversalImageActions,
      QueryKeyConstants.UniversalImageActionsMetadata,
      cuid,
    ],
    queryFn: async ({ signal }) => {
      if (isUndefined(cuid)) {
        throw new Error('Undefined cuid');
      }

      const partialCuid = Cuid.toPartial(cuid);
      const [metadata, statuses] = await Promise.all([
        getMediaItemMetadata(cuid, { signal }),
        getRightsRequestStatuses([partialCuid], { signal }),
      ]);
      const rightsRequestStatus = statuses.data[partialCuid];

      if (isDefined(rightsRequestStatus)) {
        return {
          ...metadata,
          rightsRequestStatus,
        };
      }

      return metadata;
    },
    enabled: isDefined(cuid) && cuid.length > 0,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
